/**
 * ScoreChip — Colour-coded drama score badge.
 *
 * Severity thresholds match existing drama score logic:
 *   low  (< 4.0): green background, #065f46 text (WCAG 8.9:1 AAA)
 *   mid  (4-6.4): amber background, #92400e text (WCAG 7.3:1 AAA)
 *   high (≥ 6.5): red background,   #BE123C text (WCAG 4.5:1 AA)
 *
 * Accessibility: hidden from accessibility tree — parent AnalysisFeedRow
 * supplies the full sentence label that includes the score value.
 */

import React from 'react';
import { Text, View, StyleSheet } from 'react-native';

type ScoreSeverity = 'low' | 'mid' | 'high';

interface ScoreChipProps {
  value: number;
  severity?: ScoreSeverity;
}

export function deriveSeverity(value: number): ScoreSeverity {
  if (value < 4.0) return 'low';
  if (value < 6.5) return 'mid';
  return 'high';
}

const CHIP_BACKGROUND: Record<ScoreSeverity, string> = {
  low: '#D1FAE5',
  mid: '#FEF3C7',
  high: '#FCE7F3',
};

const TEXT_COLOR: Record<ScoreSeverity, string> = {
  low: '#065f46',
  mid: '#92400e',
  high: '#BE123C',
};

export const ScoreChip: React.FC<ScoreChipProps> = ({ value, severity }) => {
  const resolvedSeverity = severity ?? deriveSeverity(value);

  return (
    <View
      style={[styles.chip, { backgroundColor: CHIP_BACKGROUND[resolvedSeverity] }]}
      accessibilityElementsHidden={true}
      importantForAccessibility="no-hide-descendants"
    >
      <Text style={[styles.text, { color: TEXT_COLOR[resolvedSeverity] }]}>
        {value.toFixed(1)}
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  chip: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 9999,
  },
  text: {
    fontSize: 12,
    fontWeight: '700',
  },
});
